// 📄 context/ProjectsContext.tsx
// Lista GLOBAL de los proyectos del usuario autenticado (todos, sin
// importar rol), a diferencia de ProjectContext.tsx (singular) que solo
// conoce el proyecto actual y depende de useParams (:projectId) — por
// eso solo existe dentro de /proyecto/:projectId/*. Este vive junto a
// AuthProvider (ver index.js), disponible en cualquier punto de la app
// —lo necesita, entre otros, Nexus (el asistente IA) para resolver
// proyectos por nombre sin tener que suscribirse él mismo (ver GeminiChat.tsx).
import React, { createContext, useContext, useEffect, useState } from 'react';
import { subscribeProjectsForUser, Project, ProjectRole } from '../services/projectService.ts';
import { useAuth } from './AuthContext.tsx';

export interface UserProjectInfo extends Project {
  // Rol del usuario autenticado en ESTE proyecto — se resuelve una sola
  // vez acá para que cada consumidor no tenga que repetir el
  // `miembros.find(...)`.
  miRol: ProjectRole | null;
}

interface ProjectsContextValue {
  proyectos: UserProjectInfo[];
  loading: boolean;
}

const ProjectsContext = createContext<ProjectsContextValue>({ proyectos: [], loading: true });

export function ProjectsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [proyectos, setProyectos] = useState<UserProjectInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setProyectos([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = subscribeProjectsForUser(user.uid, (lista) => {
      setProyectos(
        lista.map((p) => ({
          ...p,
          miRol: p.miembros.find((m) => m.userId === user.uid)?.rol ?? null
        }))
      );
      setLoading(false);
    });
    return () => unsubscribe();
  }, [user]);

  return (
    <ProjectsContext.Provider value={{ proyectos, loading }}>
      {children}
    </ProjectsContext.Provider>
  );
}

export function useUserProjects() {
  return useContext(ProjectsContext);
}
